/**
 * Rate Review Form Validation Utility
 * @module RateReviewValidation
 */

import { RATE_REVIEW_CONSTANTS } from "../constants/rateReviewConstants";

const REGEX = {
  NAME_WITH_SPACES: /^[A-Za-z\s]+$/,
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  PHONE: /^[0-9]{10}$/,
  GATEWAY: new RegExp(`^[0-9]{${RATE_REVIEW_CONSTANTS.GATEWAY_ID_LENGTH}}$`),
};

/**
 * Validates Gateway ID field
 * @param {string} gatewayValue - Gateway ID value to validate
 * @returns {undefined} Always returns undefined (no validation)
 */
export const validateGatewayId = (gatewayValue) => {
  return undefined;
};

/**
 * Validates Sales Representative field
 * @param {string} salesRepValue - Sales Representative value to validate
 * @param {Array<Object>} [salesRepsList=[]] - List of valid sales representatives
 * @returns {undefined} Always returns undefined (no validation)
 */
export const validateSalesRepresentative = (salesRepValue, salesRepsList = []) => {
  return undefined;
};

/**
 * Validates First Name field
 * @param {string} firstName - First name value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateFirstName = (firstName) => {
  if (!firstName || !firstName.trim()) {
    return "First name is required";
  }

  const trimmed = firstName.replace(/\s+/g, " ").trim();
  
  if (trimmed.length < 2) {
    return "First name must be at least 2 characters long";
  }

  if (!REGEX.NAME_WITH_SPACES.test(trimmed)) {
    return "First name can only contain letters";
  }

  return undefined;
};

/**
 * Validates Last Name field
 * @param {string} lastName - Last name value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateLastName = (lastName) => {
  if (!lastName || !lastName.trim()) {
    return "Last name is required";
  }

  const trimmed = lastName.replace(/\s+/g, " ").trim();
  
  if (trimmed.length < 2) {
    return "Last name must be at least 2 characters long";
  }

  if (!REGEX.NAME_WITH_SPACES.test(trimmed)) {
    return "Last name can only contain letters";
  }

  return undefined;
};

/**
 * Validates Email field
 * @param {string} email - Email value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return "Email is required";
  }

  const trimmedEmail = email.trim();
  
  if (!REGEX.EMAIL.test(trimmedEmail)) {
    return "Please enter a valid email address";
  }

  return undefined;
};

/**
 * Validates Phone Number field
 * @param {string} phoneNumber - Phone number value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return "Phone number is required";
  }

  const numbersOnly = phoneNumber.replace(/[^0-9]/g, "");
  
  if (numbersOnly.length > RATE_REVIEW_CONSTANTS.PHONE_NUMBER_LENGTH) {
    return `Phone number cannot be more than ${RATE_REVIEW_CONSTANTS.PHONE_NUMBER_LENGTH} digits`;
  }
  
  if (numbersOnly.length < RATE_REVIEW_CONSTANTS.PHONE_NUMBER_LENGTH) {
    return `Phone number must be exactly ${RATE_REVIEW_CONSTANTS.PHONE_NUMBER_LENGTH} digits`;
  }
  
  if (!REGEX.PHONE.test(numbersOnly)) {
    return "Phone number can only contain digits (0-9)";
  }

  return undefined;
};

/**
 * Validates Business Name field
 * @param {string} businessName - Business name value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateBusinessName = (businessName) => {
  if (!businessName || !businessName.trim()) {
    return "Business name is required";
  }

  return undefined;
};

/**
 * Validates Notes field
 * @param {string} notes - Notes value to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateNotes = (notes) => {
  if (!notes) {
    return undefined;
  }

  if (notes.length > RATE_REVIEW_CONSTANTS.MAX_NOTES_LENGTH) {
    return `Notes cannot exceed ${RATE_REVIEW_CONSTANTS.MAX_NOTES_LENGTH} characters`;
  }

  return undefined;
};

/**
 * Validates Processing Statements files
 * @param {Array<File>} processingStatementsFiles - Array of file objects to validate
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateProcessingStatements = (processingStatementsFiles) => {
  if (!processingStatementsFiles || !Array.isArray(processingStatementsFiles) || processingStatementsFiles.length === 0) {
    return "At least one Processing Statement file is required";
  }

  return undefined;
};

/**
 * Validates all Rate Review form fields
 * @param {Object} formData - Form data object with all field values
 * @param {Object} [options={}] - Validation options
 * @param {Array<Object>} [options.salesRepsList=[]] - List of valid sales representatives
 * @param {Array<File>} [options.processingStatementsFiles=[]] - Array of Processing Statements files
 * @returns {Object<string, string>} Object with field names as keys and error messages as values
 */
export const validateRateReviewForm = (formData, options = {}) => {
  const errors = {};
  const { salesRepsList = [], processingStatementsFiles = [] } = options;

  const firstNameError = validateFirstName(formData.firstName);
  if (firstNameError) errors.firstName = firstNameError;

  const lastNameError = validateLastName(formData.lastName);
  if (lastNameError) errors.lastName = lastNameError;

  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;

  const phoneError = validatePhoneNumber(formData.phoneNumber);
  if (phoneError) errors.phoneNumber = phoneError;

  const businessNameError = validateBusinessName(formData.businessName);
  if (businessNameError) errors.businessName = businessNameError;

  const processingStatementsError = validateProcessingStatements(processingStatementsFiles);
  if (processingStatementsError) errors.processingStatements = processingStatementsError;

  if (formData.notes) {
    const notesError = validateNotes(formData.notes);
    if (notesError) errors.notes = notesError;
  }

  return errors;
};

/**
 * Validates a single field for Rate Review form
 * @param {string} fieldName - Name of the field to validate
 * @param {string} fieldValue - Value of the field to validate
 * @param {Object} [formData={}] - Complete form data object
 * @param {Object} [options={}] - Validation options
 * @param {Array<Object>} [options.salesRepsList=[]] - List of valid sales representatives
 * @returns {string|undefined} Error message if invalid, undefined if valid
 */
export const validateRateReviewField = (fieldName, fieldValue, formData = {}, options = {}) => {
  const { salesRepsList = [] } = options;

  switch (fieldName) {
    case "firstName":
      return validateFirstName(fieldValue);

    case "lastName":
      return validateLastName(fieldValue);

    case "email":
      return validateEmail(fieldValue);

    case "phoneNumber":
      return validatePhoneNumber(fieldValue);

    case "businessName":
      return validateBusinessName(fieldValue);

    case "Gateway":
      return validateGatewayId(fieldValue);

    case "salesRep":
      return validateSalesRepresentative(fieldValue, salesRepsList);

    case "notes":
      return validateNotes(fieldValue);

    case "processingStatements":
      const { processingStatementsFiles = [] } = options;
      return validateProcessingStatements(processingStatementsFiles);

    default:
      return undefined;
  }
};

